import { useTranslation } from 'next-i18next'
import clsx from 'clsx'
import { Image } from '~/elements/Image/Image'
import { useQueryClient } from '@tanstack/react-query'
import { ConfigurableProduct, useCrossSellProductsQuery } from '@magentoTypes'
import { createMagentoClient } from '~/graphql/magentoClient'
import { Headline } from '~/elements/Headline/Headline'
import { CrossSellItem } from './components/CrossSellItem'
import { ItemLoading } from './components/ItemLoading'
import { useNavigationQuery } from '@contentfulTypes'
import { DEFAULT_LOCALE } from '~/config/constants'
import { createGraphQLClient } from '~/graphql/contentfulClient'
import { usePreviewMode } from '~/hooks/usePreviewMode'
import { useRouter } from 'next/router'
import { createLanguageRegionLocale } from '~/lib/createLanguageRegionLocale'

type Props = {
  hasIcon?: boolean
  className?: string
  listClassName?: string
  productSize?: string
}

const underlayProducts = [
  {
    sku: '60006263-41101-14058',
    size: '240x340',
  },
  {
    sku: '60006263-41101-19747',
    size: '190x290',
  },
  {
    sku: '60006263-41101-19748',
    size: '160x225',
  },
  {
    sku: '60006263-41101-14010',
    size: '120x170',
  },
  {
    sku: '60006263-41101-14101',
    size: '70x240',
  },
  {
    sku: '60006263-41101-14097',
    size: '70x140',
  },
]

export const CrossSell = ({ hasIcon, className, listClassName, productSize }: Props) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const router = useRouter()
  const preview = usePreviewMode()
  const contentfulClient = createGraphQLClient({ preview })
  const finalLocale = router.locale ? createLanguageRegionLocale(router.locale) : DEFAULT_LOCALE
  const { data: contentfulData } = useNavigationQuery(contentfulClient, {
    preview,
    locale: finalLocale,
  })
  const magentoClient = createMagentoClient({ usePost: false, queryClient, locale: finalLocale })
  let skus = contentfulData?.pageCollection?.items?.[0]?.crossSellProducts as string | string[]
  let bestFitUnderlay = [] as any[]
  if (!skus && productSize) {
    const size = productSize.replace('cm', '')
    const lowerFitUnderlay = underlayProducts.filter(
      (v) => parseInt(v.size) < parseInt(productSize) && v.size < size,
    )
    const higherFitUnderlay = underlayProducts.filter(
      (v) => parseInt(v.size) > parseInt(productSize) && v.size > size,
    )

    if (lowerFitUnderlay.length > 0) bestFitUnderlay.push(lowerFitUnderlay[0])
    if (higherFitUnderlay.length > 0) bestFitUnderlay = [...bestFitUnderlay, ...higherFitUnderlay]

    if (process.env.NEXT_PUBLIC_STORE_CODE == 'benuta_de' && bestFitUnderlay.length == 0)
      skus = ['60007293-41104', '60007287-41104'] as string[]
    else if (process.env.NEXT_PUBLIC_STORE_CODE == 'benuta_de' && bestFitUnderlay.length > 0)
      skus = ['60007293-41104', '60007287-41104', '60006263-41101'] as string[]
    else if (bestFitUnderlay.length > 0) skus = '60006263-41101'
  }

  const { data, isFetching, error } = useCrossSellProductsQuery(
    magentoClient,
    { skus },
    {
      enabled: !!skus,
    },
  )

  if (error || !skus) return null

  return (
    <aside className={clsx(className, hasIcon && 'px-4 lg:px-0')}>
      <h4
        className={clsx(
          hasIcon && 'break-words text-[30px] font-extralight leading-none text-sg-black',
        )}
      >
        {t('Matching products')}
      </h4>
      {hasIcon ? (
        <Image
          src="/images/Icons/arrow-down.svg"
          width={26}
          height={26}
          placeholder="empty"
          className="mx-auto mt-4 mb-3 h-8 w-8"
          alt="arrow-down-icon"
        />
      ) : null}
      {isFetching ? (
        <ItemLoading />
      ) : (
        <ul className={clsx('relative flex flex-col gap-4 pt-5', listClassName)}>
          {[...(data?.products?.items || [])].reverse().map((item, i) => {
            const product = item as ConfigurableProduct
            let productVariant = -1
            if (product.variants) {
              productVariant = product.variants?.findIndex((p) => {
                for (let i = 0; i < bestFitUnderlay?.length; i++)
                  return p?.product?.sku == bestFitUnderlay?.[i]?.sku
              })
            }
            return (
              (product?.sku != '60006263-41101' ||
                (product?.sku == '60006263-41101' && productVariant != -1)) && (
                <li key={`${item?.name}_${i}`}>
                  <CrossSellItem
                    product={product}
                    variant={productVariant != -1 ? productVariant : 0}
                  />
                </li>
              )
            )
          })}
        </ul>
      )}
    </aside>
  )
}
